<template>
    <tab-content :title="$t('stock_reconciliation.admin.tab_title')">
        <g-table
            id="stock-table"
            sort-column="id"
            ref="stockTable"
            :url="`/api/admin/company/${this.$route.params.company_id}/stock`"
        >
            <b-table-column field="city" :label="$t('stock_reconciliation.admin.column_location')" width="220" v-slot="props">
                <b>{{ props.row.city }}</b><br/>
                <small>
                    {{ props.row.street }} {{ props.row.house_number }}{{ props.row.addition }}
                </small>
            </b-table-column>

            <b-table-column field="blanco_giftcards_unassigned_count" :label="$t('stock_reconciliation.admin.column_stock')" v-slot="props">
                {{ props.row.blanco_giftcards_unassigned_count - props.row.blanco_giftcards_unassigned_recent_count }}
                <small v-if="props.row.blanco_giftcards_unassigned_recent_count > 0">
                    <br/>{{ $t('restocking.gift_cards_on_their_way', { amount: props.row.blanco_giftcards_unassigned_recent_count }) }}
                </small>
            </b-table-column>

            <b-table-column field="auto_restock_giftcards" :label="$t('stock_reconciliation.admin.column_auto_restock')" v-slot="props">
                <div v-if="props.row.auto_restock_giftcards" class="tag is-success">{{ $t('stock_reconciliation.admin.status_active') }}</div>
                <div v-else class="tag">{{ $t('stock_reconciliation.admin.status_inactive') }}</div>
            </b-table-column>

            <b-table-column numeric label="" v-slot="props">
                <div class="buttons">
                    <button
                        class="button is-small"
                        :title="$t('stock_reconciliation.admin.mark_lost_button')"
                        :aria-label="$t('stock_reconciliation.admin.mark_lost_button')"
                        @click="openReconciliationForm(props.row, 'mark_lost')"
                        :disabled="props.row.blanco_giftcards_unassigned_count === 0"
                    >
                        {{ $t('stock_reconciliation.admin.mark_lost_button') }}
                    </button>

                    <button
                        class="button is-small"
                        :title="$t('stock_reconciliation.admin.scan_button')"
                        :aria-label="$t('stock_reconciliation.admin.scan_button')"
                        @click="openReconciliationForm(props.row, 'inventory_scan')"
                        :disabled="props.row.blanco_giftcards_unassigned_count === 0"
                    >
                        {{ $t('stock_reconciliation.admin.scan_button') }}
                    </button>
                </div>
            </b-table-column>
        </g-table>

        <br/><br/>
        <h3 class="tab-header">
            {{ $t('stock_reconciliation.admin.corrections_header') }}
        </h3>

        <div class="columns form">
            <div class="column is-4">
                <g-searchbox name="search_box" :placeholder="$t('stock_reconciliation.admin.search_placeholder')"/>
            </div>
        </div>

        <g-table
            id="corrections-table"
            ref="table"
            sort-column="id"
            :url="`/api/admin/company/${this.$route.params.company_id}/stock-reconciliation`"
            :filter-data="filterData"
        >
            <b-table-column field="created_at" :label="$t('stock_reconciliation.admin.column_date')" width="220" v-slot="props">
                {{ $i18nDate(new Date(props.row.created_at), 'numeric') }}
            </b-table-column>

            <b-table-column field="giftcard_count" label="" v-slot="props">
                <strong>{{ props.row.giftcard_count }}</strong> {{ $tc('stock_reconciliation.admin.cards_marked_lost', props.row.giftcard_count) }}<br/>
                <small>
                    {{ $t('stock_reconciliation.admin.action_by') }} <b>{{ `${props.row.reconciled_by.first_name} ${props.row.reconciled_by.last_name}` }}</b>
                    {{ $t('stock_reconciliation.admin.action_at') }}
                    <b>
                        {{ props.row.location.street }} {{ props.row.location.house_number }}{{ props.row.location.addition }},
                        {{ props.row.location.city }}
                    </b>
                    <template v-if="props.row.has_been_reversed">
                        <span class="tag is-light">{{ $t('stock_reconciliation.admin.fully_reversed') }}</span>
                    </template>
                </small>
            </b-table-column>

            <b-table-column numeric label="" v-slot="props">
                <div class="buttons">
                    <button
                        class="button"
                        :title="$t('stock_reconciliation.admin.confirm_reverse_text')"
                        :aria-label="$t('stock_reconciliation.admin.confirm_title')"
                        :class="{ 'is-loading': reversingReconciliationId === props.row.id }"
                        :disabled="reversingReconciliationId !== null || props.row.has_been_reversed"
                        @click="reverseReconciliation(props.row.id)"
                    >
                        <i class="icon">undo</i>
                    </button>

                    <button
                        class="button"
                        :title="$t('stock_reconciliation.detail_title')"
                        :aria-label="$t('stock_reconciliation.detail_title')"
                        @click="openReconciliation(props.row.id)"
                    >
                        <i class="icon">search</i>
                    </button>
                </div>
            </b-table-column>
        </g-table>

    </tab-content>
</template>

<script>
import TabContent from "../../../components/TabView/TabContent.vue";
import TableFilterMixins from "../../../mixins/TableFilterMixins";

export default {
    name: "Stock",

    components: {
        TabContent
    },

    mixins: [TableFilterMixins],

    data() {
        return {
            reversingReconciliationId: null,
            search_box: '',
        };
    },

    methods: {
        reverseReconciliation(reconciliationId) {
            const companyId = this.$route.params.company_id;

            this.reversingReconciliationId = reconciliationId;

            axios.get(`/api/admin/company/${companyId}/stock-reconciliation/${reconciliationId}`)
                .then(({ data: response }) => {
                    const giftcardsToRestore = response.data.giftcards
                        .filter(gc => gc.restored_at === null)
                        .map(gc => gc.id);

                    if (giftcardsToRestore.length === 0) {
                        this.$buefy.toast.open({
                            message: this.$t('stock_reconciliation.admin.no_cards_to_restore'),
                            type: 'is-warning',
                        });
                        return;
                    }

                    this.$popup('Confirmation', {
                        title: this.$t('stock_reconciliation.admin.confirm_title'),
                        text: this.$t('stock_reconciliation.admin.confirm_reverse_text'),
                        onConfirmation: () => {
                            return axios.patch(`/api/admin/company/${companyId}/stock-reconciliation/${reconciliationId}`, {
                                giftcards_to_reconcile: [],
                                giftcards_to_restore: giftcardsToRestore,
                            }).then(() => {
                                this.refreshCorrectionsTable();
                            });
                        },
                    });
                })
                .catch(() => {
                    this.$buefy.toast.open({
                        message: this.$t('stock_reconciliation.admin.fetch_error'),
                        type: 'is-danger',
                    });
                })
                .finally(() => {
                    this.reversingReconciliationId = null;
                });
        },

        openReconciliation(id) {
            this.$popup('StockReconciliationItem', {
                reconciliationId: id,
                companyId: this.$route.params.company_id,
                onClose: this.refreshCorrectionsTable,
            })
        },

        openReconciliationForm(location, operationMode) {
            this.$popup('AdminStockReconciliationForm', {
                companyId: this.$route.params.company_id,
                currentStock: location.blanco_giftcards_unassigned_count,
                currentStockRecent: location.blanco_giftcards_unassigned_recent_count,
                locationId: location.id,
                location: location,
                operationMode,
                onSuccess: this.refreshCorrectionsTable,
            })
        },

        refreshCorrectionsTable() {
            this.$refs.table.reloadCurrentPage()
            this.$refs.stockTable.reloadCurrentPage()
        },

        filterData() {
            return {
                search_box: {
                    value: this.search_box,
                    debounced: true,
                },
            };
        },
    },
}
</script>
